import { useIonToast } from '@ionic/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RegisterAndSubscribeToMachineBindingDocument,
  RegisterAndSubscribeToMachineBindingSubscriptionResult,
  RegisterAndSubscribeToMachineBindingSubscriptionVariables,
} from '../../graphql/generated';
import { useApolloSharedSubscription } from '../../subscription/apollo-subscription.manager';
import {
  RobotOperationBindingResult,
  isErrorResult,
  isExecuteFlowStatusFeedbackResult,
} from './robot-operation.binding';

export const useRobotOperationMonitorBinding = (
  machineId: string,
  options?: { skip?: boolean }
): RobotOperationBindingResult | undefined => {
  const variables: RegisterAndSubscribeToMachineBindingSubscriptionVariables = {
    input: {
      machineId,
      binding: {
        namespace: 'machine',
        type: 'robotOperationMonitor',
      },
    },
  };

  const { data } =
    useApolloSharedSubscription<RegisterAndSubscribeToMachineBindingSubscriptionResult>(
      {
        query: RegisterAndSubscribeToMachineBindingDocument,
        variables,
        subscriptionName: `binding-machine-robotOperationMonitor-${machineId}`,
        skip: options?.skip,
      }
    );

  return data?.registerAndSubscribeToMachineBindingResult?.data;
};

/**
 * Hook that returns the current execution index of the robot operation flow.
 */
export const useRobotOperationFlowExecutionIndex = (
  machineId: string,
  options?: { skip?: boolean }
): number | undefined => {
  const result = useRobotOperationMonitorBinding(machineId, options);

  return useMemo(() => {
    if (!result) {
      return;
    }

    if (!isExecuteFlowStatusFeedbackResult(result)) {
      return;
    }

    return result.feedbackData.executionIndex;
  }, [result]);
};

/**
 * Hook that presents a toast whenever a robot operation emits an error.
 */
export const useRobotOperationAlerts = (
  machineId: string,
  options?: { skip?: boolean }
): void => {
  const { i18n } = useTranslation();
  const [presentToast] = useIonToast();

  const result = useRobotOperationMonitorBinding(machineId, options);

  useEffect(() => {
    if (isErrorResult(result)) {
      presentToast({
        message: result.errorMessage,
        color: 'danger',
        position: 'top',
        buttons: [
          {
            role: 'cancel',
            text: i18n.t('common:close'),
          },
        ],
      });
    }
  }, [result, i18n, presentToast]);
};
